import { useBooGradientStyle } from "@/hooks/useBooGradientStyle";
import type { CSSProperties, ReactNode } from "react";

type LogoProps = { className?: string };

/**
 * Animated Boo brand mark — a colored blob whose 3-stop linear gradient flows
 * (`boo-logo` keyframe sweeps `background-position`) and whose silhouette
 * morphs (`boo-blob` keyframe stretches `border-radius`). Color stops are
 * rotated in JS for fresh hues; transitions are smoothed via the registered
 * `--boo-c*` properties in `index.css`.
 */
export function BooLogo({ className }: LogoProps) {
  const style: CSSProperties = {
    ...useBooGradientStyle(),
    animation:
      "boo-logo 4s ease-in-out infinite, boo-blob 12s ease-in-out infinite",
  };

  return <div aria-label="Boo" className={className} style={style} />;
}

type TextProps = { className?: string; children: ReactNode };

/**
 * The same flowy gradient as the Boo blob, but applied as the fill of text
 * via `background-clip: text`. The text characters silhouette the moving
 * gradient — useful for a hero greeting or title accent.
 *
 * Renders an inline `<span>` so it composes inside an existing heading
 * without adding layout chrome. Apply font sizing / weight via `className`
 * on the consumer side.
 */
export function BooGradientText({ className, children }: TextProps) {
  const style: CSSProperties = {
    ...useBooGradientStyle(),
    animation: "boo-logo 4s ease-in-out infinite",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    WebkitTextFillColor: "transparent",
  };

  return (
    <span className={className} style={style}>
      {children}
    </span>
  );
}
